/**
 * Старые строки анализа, которые повторяют встроенный критерий (#2518; план
 * `docs/plans/2026-10-18-builtin-criteria-e2.md`, решение 8): уведомление о
 * записи, TCPA, конфиденциальность, мат и коды обязательных фраз. У
 * организации с Э2 встроенный виден либо строкой в группе «Критерии» (ответ),
 * либо старой строкой (`legacy`) — не обоими. Одно правило для панели звонка и
 * PDF.
 *
 * `sources` — `builtinSources` ответа `GET /records/:id/criteria`. Его нет —
 * Э2 у организации выключен, ответ откатанного edge-api или запрос критериев
 * не удался: старые строки как раньше.
 *
 * Отличия от решения 8 — слово владельца 19.10:
 * - `pausedByAelo` (`builtinPausedByAelo` ответа) — ключи встроенных на паузе
 *   Aelo сейчас. Их старая строка без ответа в группе остаётся, с подписью «на
 *   паузе Aelo до повторного замера» (`pausedByAeloRow`): источник `none`
 *   прятал бы вердикт без замены.
 * - Мат: `agent_profanity` спрашивает только о сотруднике, поэтому без старого
 *   поля строка мата остаётся с репликами клиента (`legacyProfanity`).
 */
import {
  LEGACY_PHRASE_CODES,
  PHRASE_CODE_BUILTIN_KEYS,
  type BuiltinCriterionKey,
} from '@aica/core/call-criteria';
import type { BuiltinResultSources } from '@aica/api-types/schemas';
import type { XMLAnalysisResult } from '@/components/details/AnalysisPanel';

/** Ключи встроенных на паузе Aelo сейчас (`builtinPausedByAelo` ответа). */
export type BuiltinPausedByAelo = readonly string[] | undefined;

/**
 * Старая строка ключа на паузе Aelo: Э2 есть, встроенный не отвечен в группе.
 * Строка видна (`showsLegacyRow`) и подписана «на паузе Aelo до повторного
 * замера».
 */
export function pausedByAeloRow(
  sources: BuiltinResultSources | undefined,
  key: BuiltinCriterionKey,
  pausedByAelo: BuiltinPausedByAelo
): boolean {
  return (
    sources !== undefined &&
    sources[key] !== 'answer' &&
    pausedByAelo?.includes(key) === true
  );
}

/**
 * Старая строка ключа видна: Э2 выключен, результат ключа — старое поле или
 * встроенный на паузе Aelo без ответа в группе.
 */
export function showsLegacyRow(
  sources: BuiltinResultSources | undefined,
  key: BuiltinCriterionKey,
  pausedByAelo?: BuiltinPausedByAelo
): boolean {
  return (
    sources === undefined ||
    sources[key] === 'legacy' ||
    pausedByAeloRow(sources, key, pausedByAelo)
  );
}

interface MandatoryPhrases {
  used?: string[];
  missing?: string[];
  missingCodes?: string[];
  score?: number;
}

const PHRASE_KEYS: Readonly<Partial<Record<string, BuiltinCriterionKey>>> =
  PHRASE_CODE_BUILTIN_KEYS;

/**
 * Строка обязательных фраз — одна на несколько встроенных, поэтому у
 * организации с Э2 она фильтруется по кодам: в «не прозвучали» остаются коды
 * ключей со старым полем (код без встроенного — тоже, ему не с чем совпасть).
 * Свободный текст — «прозвучали» и `missing` анализа без кодов — по ключам не
 * делится, поэтому он и балл строки остаются, только когда старым полем оба
 * ключа, которые старое поле читает из этой строки (`company_named`,
 * `callback_offered`), и ни один ключ строки не отвечен в группе. Ключ без
 * результата (`none`: пауза Aelo, «вне области») свободный текст не прячет —
 * он нигде больше не показан. Пустой результат — строки нет.
 */
export function legacyMandatoryPhrases<P extends MandatoryPhrases>(
  phrases: P | undefined,
  sources: BuiltinResultSources | undefined,
  pausedByAelo?: BuiltinPausedByAelo
): MandatoryPhrases | undefined {
  if (!phrases || sources === undefined) return phrases;
  const whole =
    Object.keys(LEGACY_PHRASE_CODES).every((key) => sources[key] === 'legacy') &&
    Object.values(PHRASE_CODE_BUILTIN_KEYS).every((key) => sources[key] !== 'answer');
  return {
    ...phrases,
    used: whole ? phrases.used : [],
    missing: whole ? phrases.missing : [],
    missingCodes: phrases.missingCodes?.filter((code) => {
      const key = PHRASE_KEYS[code];
      return key === undefined || showsLegacyRow(sources, key, pausedByAelo);
    }),
    score: whole ? phrases.score : undefined,
  };
}

/** Коды «не прозвучали», чей встроенный на паузе Aelo, — для подписи под строкой. */
export function pausedByAeloPhraseCodes(
  missingCodes: readonly string[] | undefined,
  sources: BuiltinResultSources | undefined,
  pausedByAelo: BuiltinPausedByAelo
): string[] {
  return (missingCodes ?? []).filter((code) => {
    const key = PHRASE_KEYS[code];
    return key !== undefined && pausedByAeloRow(sources, key, pausedByAelo);
  });
}

/** Есть ли что показать в строке фраз (коды — в приоритете над свободным текстом). */
export function mandatoryPhrasesVisible(phrases: MandatoryPhrases | undefined): boolean {
  if (!phrases) return false;
  const missing =
    phrases.missingCodes !== undefined ? phrases.missingCodes : (phrases.missing ?? []);
  return (phrases.used?.length ?? 0) > 0 || missing.length > 0;
}

type Profanity = NonNullable<XMLAnalysisResult['speech']>['profanityDetection'];

/**
 * Строка мата анализа. Без Э2, при старом поле `agent_profanity` или его паузе
 * Aelo — целиком. Иначе мат сотрудника стоит в группе «Критерии», а строка
 * остаётся с репликами КЛИЕНТА: встроенный о нём не спрашивает, и он нигде
 * больше не показан. Реплики без говорящего (`unknown`) уходят вместе с
 * сотрудником — о клиенте они не утверждают. Клиент не ругался — строки нет:
 * «не обнаружено» читалось бы как вердикт о всём разговоре.
 */
export function legacyProfanity(
  result: XMLAnalysisResult,
  sources: BuiltinResultSources | undefined,
  pausedByAelo?: BuiltinPausedByAelo
): Profanity {
  const profanity = result.speech?.profanityDetection;
  if (!profanity || showsLegacyRow(sources, 'agent_profanity', pausedByAelo)) {
    return profanity;
  }
  const instances = (profanity.instances ?? []).filter(
    (instance) => instance.speaker === 'client'
  );
  return profanity.detected && instances.length > 0
    ? { ...profanity, instances }
    : undefined;
}

type Compliance = NonNullable<XMLAnalysisResult['compliance']>;

/** Строки карточки «Комплаенс», которые видны после правила Э2. */
export interface VisibleCompliance {
  recordingDisclaimer?: Compliance['recordingDisclaimer'];
  tcpaCompliance?: Compliance['tcpaCompliance'];
  dataPrivacy?: Compliance['dataPrivacy'];
  crmCompleteness?: Compliance['crmCompleteness'];
  mandatoryPhrases?: MandatoryPhrases;
  /** Карточка рисуется: без Э2 — всегда, как раньше; с Э2 — хоть одна строка есть. */
  any: boolean;
}

/**
 * Карточка «Комплаенс» после правила Э2: проверка видна, если применима
 * (`applicable === false` скрывает её и без Э2) и её ключ — старое поле.
 * Один расчёт для карточки и для группы «Инсайты» панели: группа без
 * карточки не должна оставаться пустой вкладкой.
 */
export function visibleCompliance(
  result: XMLAnalysisResult,
  sources: BuiltinResultSources | undefined,
  pausedByAelo?: BuiltinPausedByAelo
): VisibleCompliance | null {
  const compliance = result.compliance;
  if (!compliance) return null;
  const check = <T extends { applicable?: boolean } | undefined>(
    key: BuiltinCriterionKey,
    value: T
  ): T | undefined =>
    value && value.applicable !== false && showsLegacyRow(sources, key, pausedByAelo)
      ? value
      : undefined;
  const rows = {
    recordingDisclaimer: check('recording_disclosed', compliance.recordingDisclaimer),
    tcpaCompliance: check('tcpa_compliant', compliance.tcpaCompliance),
    dataPrivacy: check('data_privacy_violation', compliance.dataPrivacy),
    crmCompleteness: compliance.crmCompleteness,
    mandatoryPhrases: legacyMandatoryPhrases(
      result.speech?.mandatoryPhrases,
      sources,
      pausedByAelo
    ),
  };
  return {
    ...rows,
    any:
      sources === undefined ||
      !!(
        rows.recordingDisclaimer ||
        rows.tcpaCompliance ||
        rows.dataPrivacy ||
        rows.crmCompleteness
      ) ||
      mandatoryPhrasesVisible(rows.mandatoryPhrases),
  };
}

/**
 * Строки карточки «Анализ речи», кроме соотношения речи (его считает карточка
 * по диаризации), после правила Э2. Мат может оказаться единственной строкой
 * анализа речи — убранный, он не должен оставлять пустую карточку и пустую
 * вкладку «Речь». Без Э2 — как раньше: карточка есть при любом `speech`.
 */
export function speechMetricsVisible(
  result: XMLAnalysisResult,
  sources: BuiltinResultSources | undefined,
  pausedByAelo?: BuiltinPausedByAelo
): boolean {
  const speech = result.speech;
  if (!speech) return false;
  if (sources === undefined) return true;
  return !!(
    result.emotional?.interruptions ||
    speech.fillerWords ||
    speech.stopWords ||
    legacyProfanity(result, sources, pausedByAelo)
  );
}
